import { HelpCircle, BookOpen, Video, Mail, ExternalLink, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const FAQS = [
  {
    q: "How do I create a timetable?",
    a: "Go to Onboarding or Settings and create a new timetable. Define your week structure (A/B weeks), add periods and breaks for each day, and assign courses to each slot.",
  },
  {
    q: "What is an NCCD record?",
    a: "NCCD (Nationally Consistent Collection of Data) records document adjustments made for students with disability. These records support compliance evidence under the Australian Disability Standards for Education.",
  },
  {
    q: "Can I export my records for compliance?",
    a: "Yes! Go to Exports to download your records as CSV or Excel files. You can filter by date range, record type, student, and class. (Pro plan required.)",
  },
  {
    q: "What does the AI Polish feature do?",
    a: "AI Polish converts your rough shorthand notes into professional, school-appropriate documentation. Just type your quick notes, click 'Polish with AI', and it rewrites them in compliance-ready language. (Pro plan required.)",
  },
  {
    q: "How do I add students to a class?",
    a: "Go to Classes, select a class, then use 'Add Students' to search for and enroll students. You can also manage students from the Students page.",
  },
  {
    q: "What is the Diary used for?",
    a: "The Diary is your daily planner. It maps your timetable slots to each day, letting you write lesson notes, record what was taught, and link resources to specific periods.",
  },
  {
    q: "How does the Head Teacher / School Overview work?",
    a: "Schools are set up with a school code and head teacher PIN by the ClassTrack team. Teachers enter their school code in Settings. Head teachers also enter their PIN to unlock the School Overview dashboard, which shows compliance stats and record upload rates across the whole school.",
  },
  {
    q: "How do I upgrade to Pro?",
    a: "If you are a founding beta user, no payment is needed — your access is confirmed until 1 Jan 2027. For other teachers, go to Settings → Subscription and click 'Subscribe to Pro' to complete a secure checkout. Access is activated immediately after payment.",
  },
];

export default function Help() {
  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <HelpCircle className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold text-gray-900">Help & Support</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        <Card className="hover:shadow-md transition-shadow group">
          <CardContent className="p-5 flex flex-col items-center text-center gap-3">
            <div className="p-3 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors relative">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center justify-center gap-2">
                <p className="font-semibold text-gray-900">Documentation</p>
                <Badge className="bg-amber-100 text-amber-700 border-0 text-xs gap-1">
                  <Clock className="h-2.5 w-2.5" />Coming soon
                </Badge>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Guides and tutorials</p>
            </div>
            <Button variant="outline" size="sm" className="w-full gap-1 mt-auto opacity-50 cursor-not-allowed" disabled>
              <ExternalLink className="h-3 w-3" />
              Open Docs
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow group">
          <CardContent className="p-5 flex flex-col items-center text-center gap-3">
            <div className="p-3 bg-purple-50 rounded-xl group-hover:bg-purple-100 transition-colors">
              <Video className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <div className="flex items-center justify-center gap-2">
                <p className="font-semibold text-gray-900">Video Tutorials</p>
                <Badge className="bg-amber-100 text-amber-700 border-0 text-xs gap-1">
                  <Clock className="h-2.5 w-2.5" />Coming soon
                </Badge>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Step-by-step guides</p>
            </div>
            <Button variant="outline" size="sm" className="w-full gap-1 mt-auto opacity-50 cursor-not-allowed" disabled>
              <ExternalLink className="h-3 w-3" />
              Watch Videos
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow group">
          <CardContent className="p-5 flex flex-col items-center text-center gap-3">
            <div className="p-3 bg-green-50 rounded-xl group-hover:bg-green-100 transition-colors">
              <Mail className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Contact Support</p>
              <p className="text-xs text-gray-500 mt-0.5">Get help from our team</p>
            </div>
            <Button variant="outline" size="sm" className="w-full gap-1 mt-auto" asChild>
              <a href="mailto:classtrack.com.au@gmail.com">
                <Mail className="h-3 w-3" />
                Email Us
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>

      <h2 className="text-lg font-bold text-gray-900 mb-4">Getting the most out of ClassTrack</h2>
      <div className="space-y-4 mb-8">
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-1.5">
          <p className="text-sm font-semibold text-gray-900">Courses</p>
          <p className="text-sm text-gray-600 leading-relaxed">
            Create a Course for each subject you teach (e.g. English Year 10, Mathematics Year 9). Courses are the building block — they attach to timetable slots, diary entries, and program registers. A single course can appear across multiple classes. You can archive courses at the end of a year to keep things tidy.
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-1.5">
          <p className="text-sm font-semibold text-gray-900">Classes</p>
          <p className="text-sm text-gray-600 leading-relaxed">
            A Class is a group of students (e.g. 10A, 10B). Enrol students into classes from the Classes page. Once enrolled, students appear on the Records page filtered by class, and class names show up alongside your timetable slots in the Diary and Lessons pages.
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-1.5">
          <p className="text-sm font-semibold text-gray-900">Students</p>
          <p className="text-sm text-gray-600 leading-relaxed">
            Add students manually or import a CSV file. Only a first name is required — surnames and student codes are optional. For privacy, consider using initials or a preferred name rather than full legal names wherever practical. Student records are separate from any school-provided data.
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-1.5">
          <p className="text-sm font-semibold text-gray-900">Diary</p>
          <p className="text-sm text-gray-600 leading-relaxed">
            The Diary maps your timetable to each teaching day. Write lesson notes for each period directly in the entry. Use <strong>Pull from program</strong> to pre-fill a diary entry with planned content from a Program Register — then, once the lesson is done, click <strong>Delivery status</strong> to log how the item was delivered (completed, partially done, postponed, etc.). This feeds your compliance evidence without double-entry.
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-1.5">
          <p className="text-sm font-semibold text-gray-900">Lessons</p>
          <p className="text-sm text-gray-600 leading-relaxed">
            The Lessons page gives you a scrollable week-by-week view of all timetable slots. Use it to plan ahead or fill in lesson notes in bulk. Like the Diary, each lesson entry supports <strong>Pull from program</strong> and <strong>Delivery status</strong> so your Program Register stays up to date automatically.
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-1.5">
          <p className="text-sm font-semibold text-gray-900">Program Registration</p>
          <p className="text-sm text-gray-600 leading-relaxed">
            Program Registers are your planned teaching programs for a course. Add items in the order you plan to teach them, including outcomes, resources, and assessment checkpoints. As you deliver each item via the Diary or Lessons, mark its delivery status. At the end of a unit, complete the register evaluation as a compliance record. Pro plan required.
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-1.5">
          <p className="text-sm font-semibold text-gray-900">Exports</p>
          <p className="text-sm text-gray-600 leading-relaxed">
            Export your records, diary entries, and program registers as CSV or PDF. The Records export is useful for NCCD evidence folders. Keep your exports minimal — only download what your school or compliance process actually needs. Data you don't export can't be lost or mishandled. Pro plan required.
          </p>
        </div>
      </div>

      <h2 className="text-lg font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
      <div className="space-y-3">
        {FAQS.map((faq, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-gray-900 mb-2">
              <span className="text-primary mr-2">Q:</span>{faq.q}
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
